// Phase 6 — Track B T-B5. Payments copy.
// Phase 7 D4 — Track D. Adds copy for the add/edit/delete dialogs, the
// statement-upload dialog, the toolbar, and the toast strings (Q-10
// sparse: destructive-only).
// Phase 7 D5 — Track D. Adds copy for the /payments/review +
// /payments/recurring routes and removes the dead `category*` dialog
// strings + `PAYMENT_CATEGORY_OPTIONS` constant (categories are fetched
// live per-user from the backend; static slugs were the wrong shape for
// the categorisation correction wire body).

export const paymentsCopy = {
  navLabel: 'Payments',
  pageTitle: 'Payments',
  pageSubtitle: 'Your manual transactions across the selected range.',
  filterLabel: 'Date range',
  filterFromLabel: 'From',
  filterToLabel: 'To',
  filterApply: 'Apply',
  filterClear: 'Clear',
  emptyTitle: 'No transactions in this range',
  emptyBody: 'Adjust the date range or add a payment from another surface.',
  loadingLabel: 'Loading payments',
  errorTitle: 'Could not load payments',
  errorBody: 'Try again in a moment.',
  retry: 'Retry',
  columns: {
    date: 'Date',
    merchant: 'Merchant',
    amount: 'Amount',
  },

  // ---- Toolbar (Phase 7 D4) -----------------------------------------------
  toolbar: {
    addLabel: 'Add payment',
    addAriaLabel: 'Add a manual payment',
    uploadLabel: 'Upload statement',
    uploadAriaLabel: 'Upload a bank statement',
    reviewLabel: 'Review',
    reviewAriaLabel: 'Review predicted categories',
    recurringLabel: 'Recurring',
    recurringAriaLabel: 'Manage recurring payments',
  },

  // ---- Add / edit dialog (Phase 7 D4) -------------------------------------
  // Phase 7 D5: removed `category*` fields. Manual payments do not carry a
  // category in the wire shape — categories are auto-assigned by the
  // backend categoriser post-save and corrected via /payments/review.
  dialog: {
    addTitle: 'Add payment',
    addDescription: 'Record a manual payment outside of statement imports.',
    editTitle: 'Edit payment',
    editDescription: 'Update the details for this payment.',
    amountLabel: 'Amount',
    amountPlaceholder: '0.00',
    amountHelp: 'Enter the amount in the major unit (e.g. 12.34).',
    currencyLabel: 'Currency',
    merchantLabel: 'Merchant',
    merchantPlaceholder: 'Where was this paid?',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Optional note',
    dueOnLabel: 'Due on',
    paidOnLabel: 'Paid on',
    recurringLabel: 'This is a recurring payment',
    submitAdd: 'Add payment',
    submitEdit: 'Save changes',
    cancel: 'Cancel',
    deleteFromEdit: 'Delete',
    invalidAmount: 'Enter a valid amount.',
    invalidDate: 'Enter a valid date (YYYY-MM-DD).',
    submitFailed: 'Could not save the payment. Try again.',
  },

  // ---- Review queue (Phase 7 D5) ------------------------------------------
  review: {
    pageTitle: 'Review queue',
    pageSubtitle:
      'DailyDeck wasn’t sure how to categorise these. Pick the right category for each.',
    backToPayments: 'Back to payments',
    loadingLabel: 'Loading review queue',
    emptyTitle: 'All caught up',
    emptyBody: 'The next statement will populate this list.',
    errorTitle: 'Could not load the review queue',
    errorBody: 'Try again in a moment.',
    retry: 'Retry',
    confidenceLabel: (percent: number): string => `${percent}% confidence`,
    pickCategoryPlaceholder: 'Pick a category',
    pickCategoryEmpty: 'No matching categories.',
    pickCategoryAriaLabel: 'Choose the correct category',
    correctionFailed: 'Could not save your correction. Try again.',
    categoriesLoadFailed: 'Could not load categories.',
  },

  // ---- Recurring payments (Phase 7 D5) ------------------------------------
  recurring: {
    pageTitle: 'Recurring payments',
    pageSubtitle:
      'Detected recurring patterns from your statements. Tune reminder lead time or archive ones you don’t want to track.',
    backToPayments: 'Back to payments',
    loadingLabel: 'Loading recurring payments',
    emptyTitle: 'No recurring payments yet',
    emptyBody:
      'When DailyDeck spots a recurring pattern in your statements it’ll appear here.',
    errorTitle: 'Could not load recurring payments',
    errorBody: 'Try again in a moment.',
    retry: 'Retry',
    showArchivedLabel: 'Show archived',
    cadenceLabels: {
      weekly: 'Weekly',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      yearly: 'Yearly',
    } as const,
    lastSeenLabel: 'Last seen',
    nextExpectedLabel: 'Next expected',
    nextExpectedUnknown: 'Not yet projected',
    daysBeforeDueLabel: 'Reminder days before',
    daysBeforeDueAriaLabel: 'Reminder days before due',
    archiveLabel: 'Archive',
    archiveAriaLabel: 'Archive recurring payment',
    unarchiveLabel: 'Restore',
    unarchiveAriaLabel: 'Restore recurring payment',
    archivedToast: (merchant: string): string => `Archived ${merchant}`,
    archiveFailed: 'Could not archive. Try again.',
    unarchiveFailed: 'Could not restore. Try again.',
    daysBeforeDueFailed: 'Could not save the reminder change. Try again.',
    archivedTagLabel: 'Archived',
  },

  // ---- Delete confirmation dialog (Phase 7 D4) ----------------------------
  deleteDialog: {
    title: 'Delete payment',
    intro: 'This permanently removes the payment record. There is no undo.',
    summaryLabel: 'Summary',
    confirm: 'Delete',
    cancel: 'Cancel',
    failed: 'Could not delete the payment. Try again.',
  },
  deletedToast: 'Payment deleted',

  // ---- Statement upload dialog (Phase 7 D4) -------------------------------
  upload: {
    triggerLabel: 'Upload statement',
    title: 'Upload bank statement',
    description: 'Drop a CSV or text-layer PDF, or pick a file from your device.',
    dropHint: 'Drag and drop a file here, or click to browse.',
    dropHintActive: 'Release to upload.',
    browseLabel: 'Browse',
    selectedLabel: 'Selected file',
    submitLabel: 'Upload',
    cancel: 'Cancel',
    progressLabel: 'Uploading',
    progressOf: 'of',
    progressEtaPrefix: 'about',
    progressEtaSuffix: 'remaining',
    progressEtaUnknown: 'estimating remaining time…',
    successTitle: 'Statement imported',
    successWithReviewBody: (count: number): string =>
      `Review ${count} ${count === 1 ? 'transaction' : 'transactions'}`,
    rejectedExtension: 'Only .csv and .pdf files are supported.',
    rejectedTooLarge: 'File is too large. Maximum size is 5 MB.',
    rejectedNoFile: 'Pick a file before uploading.',
    rejectedScannedPdf:
      "Scanned PDFs aren't supported. Export the statement as CSV or a text-layer PDF.",
    failed: 'Could not upload the statement. Try again.',
  },
} as const;

/** Maximum statement upload size — mirrors backend multer limit. */
export const STATEMENT_UPLOAD_MAX_BYTES = 5 * 1024 * 1024;

/** Currencies the dialog exposes. The backend accepts any uppercase ISO 4217
 *  3-letter code; this short list reflects the most common consumer currencies
 *  for v2.0. Extending it is a one-line PR. */
export const PAYMENT_CURRENCY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'USD', label: 'USD — US Dollar' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'GBP', label: 'GBP — Pound Sterling' },
  { value: 'JPY', label: 'JPY — Japanese Yen' },
  { value: 'CAD', label: 'CAD — Canadian Dollar' },
  { value: 'AUD', label: 'AUD — Australian Dollar' },
];

// PAYMENT_CATEGORY_OPTIONS — REMOVED in Phase 7 D5.
// Categories are seeded per-user in the backend (see
// `src/modules/payments/subsystems/categorisation/service.ts:SEED_CATEGORIES`)
// and exposed via `GET /api/modules/payments/categories`. The wire shape
// for the categorisation correction route is `{ category_id }` — a stable
// per-user id, not a slug — so a static client-side slug list cannot be
// used to drive correction. The /payments/review route fetches the live
// category list via `apiClient.payments.categories.list()`.
